// @ts-strict-ignore
import { communicationService, UapLoadStatus } from '@ad-engine/communication';
import { dsaHandler } from '../../ad-bidders';
import { AD_ENGINE_UAP_LOAD_STATUS } from '../../communication/events/events-ad-engine-uap';
import {
	BIDDERS_CALL_PER_GROUP,
	SLOT_REFRESHER_SET_MAXIMUM_SLOT_HEIGHT,
} from '../../communication/events/events-bidders';
import { AdSlotEvent, AdSlotStatus, type AdSlot } from '../models';
import { GptProvider } from '../providers';
import { logger } from '../utils';
import { context } from './context-service';
import { slotService } from './slot-service';

const logGroup = 'slot-refresher';

interface Config {
	timeoutMS?: number;
	slots?: string[];
}

interface EventPayload {
	adSlotName?: string;
	slot?: AdSlot;
}

const defaultConfig: Config = {
	timeoutMS: 28_000,
	slots: [],
};

async function isUAP(): Promise<boolean> {
	return new Promise((resolve) => {
		communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
			const isUAP = action.isLoaded || action.adProduct === 'ruap';
			resolve(isUAP);
		});
	});
}

function callBidders(slotName: string, callback: () => void) {
	const bidGroup = context.get(`slots.${slotName}.bidGroup`);

	if (!bidGroup) {
		context.set(`slots.${slotName}.bidGroup`, slotName);
	}

	communicationService.emit(BIDDERS_CALL_PER_GROUP, {
		group: context.get(`slots.${slotName}.bidGroup`),
		callback: callback,
		shouldUpdateTargetingBeforeCallback: true,
	});
}

function refreshWhenBackInViewport(adSlot: AdSlot) {
	function refresh(event) {
		if (event.slot.getSlotElementId() === adSlot.getSlotName()) {
			logger(logGroup, `${adSlot.getSlotName()} back in the viewport, refreshing.`, event);

			callBidders(adSlot.getSlotName(), () => {
				GptProvider.refreshSlot(adSlot);
				dsaHandler.rerender(adSlot);
			});

			window.googletag.pubads().removeEventListener('slotVisibilityChanged', refresh);
		}
	}
	window.googletag.pubads().addEventListener('slotVisibilityChanged', refresh);
}

export class SlotRefresher {
	config: Config;
	slotsInTheViewport: Array<string> = [];

	log(...logValues) {
		logger(logGroup, ...logValues);
	}

	isSlotRefreshable(adSlot: AdSlot) {
		return (
			this.config.slots.includes(adSlot.getSlotName()) && !adSlot.blockRefreshOnDirectSponsorship
		);
	}

	/**
	 * Removes adSlot sizes taller than the first rendered adSlot with the same slotName.
	 * @param {AdSlot} adSlot - The ad slot to adjust.
	 */
	private removeHigherSlotSizes(adSlot: AdSlot) {
		const slotName = adSlot.getSlotName();

		const slotRefresherConfig = context.get('slotConfig.slotRefresher.sizes');
		if (!slotRefresherConfig) return;

		const slotHeightLimit = slotRefresherConfig[slotName][1];
		logger(logGroup, `SlotRefresher: ${slotName} height limit: ${slotHeightLimit}`);
		if (!slotHeightLimit) return;

		/* Checks if the configured slot size is the same size or smaller than the first ad unit that was served
		 * Also checks that the height is greater than 3, to allow ad sizes such as 4x4 to be eligible for being kept through
		 * filtering, but not sizes such as 1x1 */
		const filterCallback = (size) => size[1] <= slotHeightLimit && size[1] > 3;

		const sizeKey = context.get(`slots.${adSlot.getSlotName()}.sizes`);
		if (sizeKey && sizeKey.length > 0) {
			const updatedSizeKey = sizeKey.map((sizeObject) => ({
				...sizeObject,
				sizes: sizeObject.sizes.filter(filterCallback),
			}));

			context.set(`slots.${adSlot.getSlotName()}.sizes`, updatedSizeKey);
		}

		const defaultSizeKey = context.get(`slots.${adSlot.getSlotName()}.defaultSizes`);
		if (defaultSizeKey) {
			const filteredDefaultSizes = defaultSizeKey.filter(filterCallback);
			context.set(`slots.${adSlot.getSlotName()}.defaultSizes`, filteredDefaultSizes);
		}

		/* Emits an event that triggers a slot size update for both Prebid and A9 ad slot sizes.
		 * This will ensure that the configured ad slot sizes for each of the bidders are adjusted
		 * based on the derived slot height limit. */
		communicationService.emit(SLOT_REFRESHER_SET_MAXIMUM_SLOT_HEIGHT, {
			adSlot,
			filterCallback,
		});
	}

	addSlotSizeToContext(adSlot: AdSlot) {
		if (context.get('experiments.refreshAdhesion50') && adSlot.getSlotName() === 'floor_adhesion') {
			context.set(`slotConfig.slotRefresher.sizes.${adSlot.getSlotName()}`, [
				adSlot.getElement().clientWidth,
				50,
			]);

			return;
		}

		context.set(`slotConfig.slotRefresher.sizes.${adSlot.getSlotName()}`, [
			adSlot.getElement().clientWidth,
			adSlot.getElement().clientHeight,
		]);
	}

	refreshSlot(adSlot: AdSlot) {
		if (!this.isSlotRefreshable(adSlot)) {
			return;
		}

		const slotSizes = context.get('slotConfig.slotRefresher.sizes') || {};

		if (!(adSlot.getSlotName() in slotSizes)) {
			setTimeout(() => {
				this.addSlotSizeToContext(adSlot);
				this.removeHigherSlotSizes(adSlot);
			}, 1000);
		}

		setTimeout(() => {
			if (!adSlot.isEnabled()) {
				return;
			}

			if (this.slotsInTheViewport.includes(adSlot.getSlotName())) {
				adSlot.updatePushTimeAfterBid();
				callBidders(adSlot.getSlotName(), () => {
					adSlot.destroyTracking();
					GptProvider.refreshSlot(adSlot);
					dsaHandler.rerender(adSlot);
				});

				return;
			}

			this.log(`refreshSlot() - ${adSlot.getSlotName()} waiting for being in viewport.`);
			refreshWhenBackInViewport(adSlot);
		}, this.config.timeoutMS);
	}

	private addSlotsConfiguredToRefreshing() {
		const allSlots = slotService.slotConfigsMap;
		Object.entries(allSlots).forEach(([slotKey, slotConfiguration]) => {
			if (!slotConfiguration.slotRefreshing) return;
			this.config.slots.push(slotKey);
		});
	}

	setupSlotRefresher(additionalConfig: Config, isUap: boolean, logger) {
		this.config = {
			...defaultConfig,
			...additionalConfig,
		};

		this.addSlotsConfiguredToRefreshing();

		const disabled = this.config.slots.length < 1;
		if (disabled || isUap) {
			logger('disabled');
			return;
		}

		communicationService.onSlotEvent(AdSlotEvent.SLOT_VIEWED_EVENT, ({ slot }: EventPayload) => {
			if (!this.isSlotRefreshable(slot)) return;

			logger(`${slot.getSlotName()} viewed`);
			this.refreshSlot(slot);
		});

		communicationService.onSlotEvent(AdSlotStatus.STATUS_COLLAPSE, ({ slot }: EventPayload) => {
			if (!this.isSlotRefreshable(slot)) return;
			logger(`${slot.getSlotName()} collapse`);
		});

		communicationService.onSlotEvent(
			AdSlotEvent.SLOT_BACK_TO_VIEWPORT,
			({ slot }: EventPayload) => {
				if (!this.isSlotRefreshable(slot)) return;

				this.slotsInTheViewport.push(slot.getSlotName());
			},
		);

		communicationService.onSlotEvent(AdSlotEvent.SLOT_LEFT_VIEWPORT, ({ slot }: EventPayload) => {
			if (!this.isSlotRefreshable(slot)) return;
			this.slotsInTheViewport = this.slotsInTheViewport.filter(
				(slotName) => slotName !== slot.getSlotName(),
			);
		});

		logger('enabled', this.config);
	}

	async init() {
		this.setupSlotRefresher(context.get('services.slotRefresher.config'), await isUAP(), this.log);
	}
}

export const slotRefresher = new SlotRefresher();
